import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, ListNumbersIcon as ListNumbers, ArrowsClockwiseIcon as ArrowsClockwise, CircleNotchIcon as Spinner, WarningIcon as Warning } from "@phosphor-icons/react";
import { getScreenWalkthrough, getWorkspace, listScreenWalkthroughs, listWorkspaceMembers, rebuildScreenWalkthroughs } from "@/lib/api";
import { formatApiErrorDetail, useAuth } from "@/lib/auth";
import { ORDER_WORDING } from "@/lib/screenWalkthrough";
import WalkthroughSummaryCards from "@/components/screenwalkthrough/WalkthroughSummaryCards";
import ProgramPicker from "@/components/screenwalkthrough/ProgramPicker";
import StepCard from "@/components/screenwalkthrough/StepCard";
import StepDetail from "@/components/screenwalkthrough/StepDetail";

/** Iter 47 — each program's observed screens in source order, with input / output fields and proven DB mappings. */
export default function WorkspaceScreenWalkthrough() {
  const { id: workspaceId } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [index, setIndex] = useState(null);
  const [walk, setWalk] = useState(null);
  const [walkError, setWalkError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingWalk, setLoadingWalk] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  const program = (params.get("program") || "").toUpperCase();
  const query = params.get("q") || "";
  const stepSeq = Number(params.get("step")) || null;
  const canWrite = user?.role === "admin" || myRole === "owner" || myRole === "editor";

  useEffect(() => {
    getWorkspace(workspaceId).then((d) => setWorkspace(d?.workspace || null)).catch(() => {});
    listWorkspaceMembers(workspaceId)
      .then((m) => setMyRole((m.members || []).find((x) => x.user_id === user?.id)?.role || null))
      .catch(() => setMyRole(null));
  }, [workspaceId, user]);

  const loadIndex = useCallback(async () => {
    setLoading(true);
    try {
      setIndex(await listScreenWalkthroughs(workspaceId, { limit: 1 }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not load screen walkthroughs");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);
  useEffect(() => { loadIndex(); }, [loadIndex]);

  const loadWalk = useCallback(async () => {
    if (!program) { setWalk(null); setWalkError(null); return; }
    setLoadingWalk(true);
    setWalkError(null);
    try {
      setWalk(await getScreenWalkthrough(workspaceId, program));
    } catch (e) {
      setWalk(null);
      setWalkError(formatApiErrorDetail(e) || "Could not load the walkthrough");
    } finally {
      setLoadingWalk(false);
    }
  }, [workspaceId, program]);
  useEffect(() => { loadWalk(); }, [loadWalk]);

  const updateParams = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === "" || v === null || v === undefined) next.delete(k); else next.set(k, String(v));
    }
    setParams(next, { replace: true });
  };
  const selectProgram = (name) => updateParams({ program: name, step: "" });
  const selectedStep = useMemo(() => (walk?.steps || []).find((s) => s.sequence_number === stepSeq) || null, [walk, stepSeq]);

  const handleRebuild = async () => {
    if (!window.confirm("Rebuild the screen walkthrough? This re-runs the screen format bindings and field references, then renumbers the steps from the observed RPG statements. Idempotent; manual rows are never touched.")) return;
    setRebuilding(true);
    try {
      const r = await rebuildScreenWalkthroughs(workspaceId);
      toast.success(`Walkthrough: ${r.inserted} new, ${r.updated} updated, ${r.deleted} removed · ${r.total} steps across ${r.programs} program${r.programs === 1 ? "" : "s"} · ${r.bound} bound, ${r.ambiguous} ambiguous, ${r.unresolved} unresolved.`);
      loadIndex();
      loadWalk();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  const programs = index?.programs || [];
  useEffect(() => {
    if (!program && programs.length > 0) selectProgram(programs[0].program_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programs.length, program]);

  return (
    <div className="space-y-6" data-testid="screen-walkthrough-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to={`/workspaces/${workspaceId}/metadata/screen-bindings`} className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] inline-flex items-center gap-1" data-testid="sw-back-to-bindings">
            <ArrowLeft size={11} weight="bold" /> Back to screen format bindings
          </Link>
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight tracking-tight mt-3">
            <ListNumbers size={26} weight="duotone" color="#0f62fe" className="inline-block mr-2 align-text-bottom" />
            Screen walkthrough
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-3xl">
            Each program's screens in the order its RPG statements appear — which record format is shown, which DSPF defines it, which fields the
            user can type into, which are displayed back, and which map to database fields through REF / REFFLD. Built only from observed screen
            format bindings and parsed DDS. <strong className="text-[var(--ml-text-primary)]">{ORDER_WORDING}</strong> No LLM inference.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] text-right">
            Workspace<br /><span className="text-[var(--ml-text-primary)]">{workspace?.name || "—"}</span>
          </div>
          {canWrite && (
            <button type="button" onClick={handleRebuild} disabled={rebuilding} className="ml-btn-secondary flex items-center gap-1.5" data-testid="sw-rebuild-btn"
              title="Rebuild bindings, field references and walkthrough steps. Idempotent.">
              {rebuilding ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
              {rebuilding ? "Rebuilding…" : "Rebuild walkthrough"}
            </button>
          )}
          <Link to={`/workspaces/${workspaceId}/metadata/field-dependencies`} className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline" data-testid="sw-field-deps-link">
            Field dependencies →
          </Link>
        </div>
      </div>

      {loading && !index ? (
        <div className="ml-card p-8 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]" data-testid="sw-loading"><Spinner size={18} className="animate-spin mr-2" /> Loading screen walkthroughs…</div>
      ) : (
        <WalkthroughSummaryCards summary={index?.summary} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1">
          <ProgramPicker programs={programs} query={query} onQuery={(q) => updateParams({ q })} selected={program} onSelect={selectProgram} />
        </div>
        <div className="lg:col-span-3 space-y-4">
          {!program && <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="sw-no-program">Pick a program to see its screens in observed order.</div>}
          {program && loadingWalk && !walk && <div className="ml-card p-8 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]" data-testid="sw-walk-loading"><Spinner size={18} className="animate-spin mr-2" /> Loading {program}…</div>}
          {program && walkError && <div className="ml-card p-6 text-sm text-[#a2191f]" data-testid="sw-walk-error">{walkError}</div>}
          {walk && (
            <>
              <div className="ml-card px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="sw-program-header" data-program={walk.program_name}>
                <div>
                  <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Program</div>
                  <div className="text-xl font-semibold ml-mono" data-testid="sw-program-name">{walk.program_library ? `${walk.program_library}/` : ""}{walk.program_name}</div>
                </div>
                <div className="text-xs text-[var(--ml-text-secondary)]" data-testid="sw-program-totals">
                  {walk.totals.steps} screen statement{walk.totals.steps === 1 ? "" : "s"} · {walk.totals.bound} bound · {walk.totals.ambiguous} ambiguous · {walk.totals.unresolved} unresolved ·{" "}
                  {walk.totals.display_files} display file{walk.totals.display_files === 1 ? "" : "s"} · <strong style={{ color: "#0043ce" }}>{walk.totals.input} input-capable</strong> · {walk.totals.output + walk.totals.display} output/display · {walk.totals.proven_mappings} proven · <span style={{ color: "#8a6d00" }}>{walk.totals.ambiguous_mappings} ambiguous</span> mapping{walk.totals.ambiguous_mappings === 1 ? "" : "s"}
                </div>
              </div>
              <div className={`text-xs px-3 py-2 flex items-start gap-2 ${walk.order.conditional_statements ? "bg-[#fff8e1] text-[#8a6d00]" : "bg-[var(--ml-surface)] text-[var(--ml-text-secondary)]"}`} data-testid="sw-order-note" data-conditional={walk.order.conditional_statements}>
                <Warning size={14} weight="bold" className="shrink-0 mt-0.5" />
                <span><strong>{walk.order.wording}</strong> {walk.order.note}</span>
              </div>
              <div className={`grid grid-cols-1 gap-4 ${selectedStep ? "xl:grid-cols-5" : ""}`}>
                <ol className={`space-y-2 ${selectedStep ? "xl:col-span-2" : ""}`} data-testid="sw-step-list" data-count={walk.steps.length}>
                  {walk.steps.map((s) => (
                    <li key={s.id}>
                      <StepCard step={s} selected={selectedStep?.sequence_number === s.sequence_number} onSelect={(st) => updateParams({ step: st.sequence_number })} />
                    </li>
                  ))}
                </ol>
                {selectedStep && (
                  <div className="xl:col-span-3">
                    <StepDetail workspaceId={workspaceId} step={selectedStep} onClose={() => updateParams({ step: "" })} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
